import type { Ref, ReactElement } from 'react';
import { forwardRef, memo } from 'react';

import type { ExtendedMaterial, Material } from '../../gql';

import MenuItem, { type MenuItemProps } from '@mui/material/MenuItem';

import { extractMaterialThumbnailUrl } from '../../utils/extractMaterialThumbnailUrl';
import {
    getMaterialDescription,
    getMaterialHeader,
    getMaterialTitle,
} from '../../utils/material-labels';

import { MaterialInformation } from './MaterialInformation';
import { MenuItemThumbnail } from './MenuItemThumbnail';

type MaterialType = ExtendedMaterial | Material;

interface MenuItemMaterialProps extends MenuItemProps {
    /** The material data (can be extended or bom) */
    material: MaterialType;
    /** Whether the material image is loading or not */
    isImageLoading?: boolean;
    /** The search query to highlight in the material text */
    highlight?: string;
}

const MenuItemMaterialRaw = forwardRef(
    (
        {
            material,
            isImageLoading = false,
            highlight,
            ...menuItemProps
        }: MenuItemMaterialProps,
        ref?: Ref<HTMLLIElement> | null
    ): ReactElement => {
        const headerText = getMaterialHeader(material);
        const nameText = getMaterialTitle(material);
        const detailText = getMaterialDescription(material);
        const materialThumbnail = extractMaterialThumbnailUrl(material);

        return (
            <MenuItem
                {...menuItemProps}
                ref={ref}
                style={{
                    padding: 0,
                    ...menuItemProps.style,
                }}
                value={material.materialPublicId}
                sx={{
                    m: 0,
                    flexGrow: 1,
                    minHeight: 64,
                    // eslint-disable-next-line @typescript-eslint/no-misused-spread
                    ...menuItemProps.sx,
                }}
            >
                <MenuItemThumbnail
                    alt={`attachment-for-${material.libraryRef}`}
                    src={materialThumbnail}
                    isLoading={isImageLoading}
                />
                <MaterialInformation
                    headerText={headerText}
                    nameText={nameText}
                    detailText={detailText}
                    highlight={highlight}
                />
            </MenuItem>
        );
    }
);

MenuItemMaterialRaw.displayName = 'MenuItemMaterial';

const MenuItemMaterial = memo(MenuItemMaterialRaw);

export default MenuItemMaterial;
export type { MenuItemMaterialProps };
